import { track } from "@amplitude/analytics-react-native";
import toast from "src/swsh-native/toast";
import { useCallback, useState } from "react";
import { amp } from "src/api/amplitude";
import haptic from "src/helpers/haptic";
import log from "src/helpers/log";
import { enumNever } from "src/shared/helpers/generalHelpers";
import { useShareContext } from "../../contexts/ShareContext";
import getUniqueTypes from "../../helpers/getUniqueTypes";
import sendSmsAsync from "../../helpers/sendSmsAsync";
import { typeParseData } from "../../types";
import { ShareIMessageDataType } from "./shareIMessageHelpers";

const useShareIMessage = (data: ShareIMessageDataType) => {
	const { prefetched, onComplete, onSuccess } = useShareContext();
	const [loading, setLoading] = useState(false);

	const handlePress = useCallback(async () => {
		try {
			setLoading(true);
			track(amp.share.sms, {
				types: getUniqueTypes(data),
				dataLength: data.length,
			});
			const item = data[0];
			if (!item) {
				return;
			}
			const type = item.type;
			haptic("Light");
			switch (type) {
				case "Text":
				case "Url":
					const body = type === "Text" ? item.text : item.url;
					const { result: textResult } = await sendSmsAsync([], body);
					if (textResult === "sent") {
						onSuccess?.();
						if (item.successAmplitude) {
							track(item.successAmplitude.eventName, {
								...item.successAmplitude.eventProperties,
								shareType: item.type,
								shareAction: "SMS",
							});
						}
					}
					return;
				case "Image":
				case "LivePhoto":
					const results = await Promise.all(
						typeParseData(data, type)
							.map(async (item) => {
								const { url } = item;
								return prefetched.current[url]!;
							})
							.filter((res) => !!res),
					);

					const { result: imageResult } = await sendSmsAsync([], "", {
						attachments: (
							await Promise.all(
								results.map(async (res, i) => {
									return {
										uri: (await res.uri())!,
										filename: `image${i}.${await res.ext()}`,
										mimeType: await res.contentType(),
									};
								}),
							)
						).filter((res) => !!res.uri),
					});
					if (imageResult === "sent") {
						onSuccess?.();
						for (const item of data) {
							if (item.successAmplitude) {
								track(item.successAmplitude.eventName, {
									...item.successAmplitude.eventProperties,
									shareType: item.type,
									shareAction: "SMS",
								});
							}
						}
					}
					return;
				default:
					return enumNever(type);
			}
		} catch (err) {
			if (err instanceof Error && err.message.includes("SMS sending in progress")) {
				toast({
					preset: "error",
					title: "Too many SMS attempts",
					message: "Please restart the app",
				});
			} else {
				log.error({ err }, "ShareIMessage");
				toast({ preset: "error", title: "Something went wrong" });
			}
		} finally {
			setLoading(false);
			onComplete?.();
		}
	}, [data, onSuccess, prefetched, onComplete]);

	return {
		loading,
		handlePress,
	};
};
export default useShareIMessage;
